import React from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  Stack,
  useTheme,
  CssBaseline,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DomainDisabledIcon from "@mui/icons-material/DomainDisabled";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import LoginIcon from "@mui/icons-material/Login";
import EmailIcon from "@mui/icons-material/Email";

interface CompanyNotAssignProps {
  statusCode?: number;
}

export const CompanyNotAssign = ({ statusCode }: CompanyNotAssignProps) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleContactSupport = () => {
    // Open email client to contact support
    window.location.href =
      "mailto:support@example.com?subject=Company Assignment Request";
  };

  const handleLogin = () => {
    // Navigate to login page
    navigate("/auth/login");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: theme.palette.background.default,
        py: 4,
      }}
    >
      <CssBaseline />
      <Container maxWidth="md">
        <Card
          sx={{
            textAlign: "center",
            p: 4,
            boxShadow: theme.shadows[8],
            borderRadius: 2,
          }}
        >
          <CardContent>
            {/* Main Icon */}
            <Box sx={{ mb: 4 }}>
              <DomainDisabledIcon
                sx={{ fontSize: 80, color: theme.palette.warning.main, mb: 2 }}
              />
            </Box>

            {/* Main Title */}
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: "bold",
                color: theme.palette.warning.main,
                mb: 2,
              }}
            >
              No Company Assigned
            </Typography>

            {/* Description */}
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Your account has not been assigned to any company or outlet yet.
              Please contact your administrator or support team to get assigned
              to the appropriate company-outlet.
            </Typography>

            {/* Action Buttons */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="center"
              sx={{ mb: 4 }}
            >
              <Button
                variant="contained"
                size="large"
                startIcon={<ContactSupportIcon />}
                onClick={handleContactSupport}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  "&:hover": {
                    bgcolor: theme.palette.primary.dark,
                  },
                  px: 4,
                  py: 1.5,
                }}
              >
                Contact Support
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={<LoginIcon />}
                onClick={handleLogin}
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.dark,
                    bgcolor: theme.palette.primary.light,
                  },
                  px: 4,
                  py: 1.5,
                }}
              >
                Back to Login
              </Button>
            </Stack>

            {/* Additional Information */}
            <Box
              sx={{
                bgcolor: theme.palette.warning.light,
                p: 3,
                borderRadius: 2,
                border: `1px solid ${theme.palette.warning.main}`,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
                What should you do?
              </Typography>
              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="body2" color="text.secondary">
                  • Contact your administrator to assign you to a company-outlet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Reach out to the support team for assistance
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Once assigned, you'll have full access to the system
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Check your email for any updates from the support team
                </Typography>
              </Stack>
            </Box>

            {/* Support Contact Info */}
            <Box sx={{ mt: 4 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <EmailIcon fontSize="small" />
                Need help? Email us at support@example.com
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};
